import { useAuth } from './useAuth';

// Import centralized permissions from RoleBasedRoute to avoid duplication
// Keep in sync with RoleBasedRoute.jsx
const ROUTE_PERMISSIONS = {
  admin: ['*'],
  manager: ['services', 'dispatch-queue', 'disputes', 'case-booking', 'add-service', 'create-case', 'vendor-search', 'invoices', 'case-history', 'referrals', 'users', 'vendor-registrations', 'reports'],
  agent: ['services', 'dispatch-queue', 'disputes', 'case-booking', 'add-service', 'create-case', 'vendor-search', 'case-history'],
  vendor_owner: [],
  vendor_manager: [],
  driver: [],
};

/**
 * Hook to check if current user has access to specific routes
 * Useful for conditional rendering in components and navigation
 */
export const useRoleAccess = () => {
  const { user } = useAuth();
  const userRole = user?.role?.toLowerCase();

  const checkRoleAccess = (userRole, requiredRoute) => {
    if (!userRole) return false;
    
    const userPermissions = ROUTE_PERMISSIONS[userRole] || [];
    
    // Admin has wildcard access
    if (userPermissions.includes('*')) {
      return true;
    }
    
    // Check if user has permission for the specific route
    return userPermissions.includes(requiredRoute);
  };

  const hasRouteAccess = (routeName) => {
    if (!userRole) return false;
    return checkRoleAccess(userRole, routeName);
  };

  const hasAnyRouteAccess = (routeNames) => {
    return routeNames.some(route => hasRouteAccess(route));
  };

  // Check access to specific page groups
  const canAccessMaintenancePages = () => {
    return hasAnyRouteAccess(['roles', 'common-codes', 'cause-of-calls', 'dealers', 'client-service-rates', 'scheme-service-setup']);
  };

  const canAccessDashboard = () => {
    return hasRouteAccess('dashboard');
  };

  const canAccessConfiguration = () => {
    return hasRouteAccess('configuration');
  };

  return {
    userRole,
    hasRouteAccess,
    hasAnyRouteAccess,
    canAccessMaintenancePages,
    canAccessDashboard,
    canAccessConfiguration,
    isAdmin: userRole === 'admin',
    isManager: userRole === 'manager',
    isAgent: userRole === 'agent',
    isVendorRole: ['vendor_owner', 'vendor_manager', 'driver'].includes(userRole),
  };
}; 